import {
    Building2,
    HeartPulse,
    GraduationCap,
    Baby,
    Siren,
    Coffee,
    HardHat,
    Home,
} from "lucide-react";

const KITCHEN_IMG =
    "https://images.unsplash.com/photo-1760001553414-5634201efc36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsfGVufDB8fHx8MTc3ODI2MDY5NHww&ixlib=rb-4.1.0&q=85";
const ARCH_IMG =
    "https://images.unsplash.com/photo-1774600166818-e554a4d4c376?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3QlMjBibHVlcHJpbnQlMjBwbGFufGVufDB8fHx8MTc3ODI2MDY5NHww&ixlib=rb-4.1.0&q=85";

const PARTNERS = [
    { icon: HardHat, name: "Fire Suppression Companies" },
    { icon: Building2, name: "Mechanical Engineers" },
    { icon: Home, name: "Architects & Designers" },
];

const FACILITIES = [
    { icon: HeartPulse, name: "Senior Care Homes", note: "Memory care & assisted living" },
    { icon: HeartPulse, name: "Hospitals", note: "Patient floors & break rooms" },
    { icon: GraduationCap, name: "University Housing", note: "Dorm common kitchens" },
    { icon: Baby, name: "Day Care Facilities", note: "Childcare & after-school" },
    { icon: Siren, name: "Fire Stations", note: "Crew quarters & firehouse kitchens" },
    { icon: Coffee, name: "Office Break Rooms", note: "Workplaces & coworking" },
    { icon: Home, name: "At-Risk Properties", note: "Group homes & shelters" },
    { icon: Building2, name: "Any Domestic Hood + Range", note: "If it cooks, we cover it" },
];

function SectionHeader() {
    return (
        <div className="grid lg:grid-cols-12 gap-8 mb-12 md:mb-16">
            <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                        / 02 — Who We Protect
                    </span>
                </div>
                <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                    Built so kitchen staff
                    <br />
                    <span className="text-[#E53935] italic font-medium">never</span> have to
                    <br />
                    evacuate.
                </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                <p className="text-base md:text-lg text-[#a1a1aa] leading-relaxed">
                    The two-stage heat sensor warns staff in time to lower the heat and avoid
                    activation entirely. If reducing heat doesn't work, Kitchensafe activates and
                    extinguishes the fire before it can spread.
                </p>
            </div>
        </div>
    );
}

function PartnerCell({ partner }) {
    return (
        <div
            data-testid={`partner-${partner.name}`}
            className="bg-[#141416] p-6 md:p-8 flex items-center gap-4 group hover:bg-[#1a1a1e] transition-colors"
        >
            <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#E53935]/40 transition-all">
                <partner.icon className="w-5 h-5 text-[#E53935]" strokeWidth={1.75} />
            </div>
            <div className="font-display font-bold text-base text-white">{partner.name}</div>
        </div>
    );
}

function PartnersRow() {
    return (
        <div className="mb-12">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#71717a] mb-5">
                Specified by
            </div>
            <div className="grid sm:grid-cols-3 gap-px bg-white/5 border border-white/10">
                {PARTNERS.map((p) => (
                    <PartnerCell key={p.name} partner={p} />
                ))}
            </div>
        </div>
    );
}

function FieldPhotoCell() {
    return (
        <div className="sm:col-span-2 lg:col-span-2 lg:row-span-2 relative bg-[#0f0f12] overflow-hidden min-h-[280px]">
            <img
                src={KITCHEN_IMG}
                alt="Commercial kitchen"
                className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935] mb-2">
                    Field Photo
                </div>
                <div className="font-display font-bold text-xl text-white">
                    Senior care break room
                    <br />
                    Phoenix, AZ
                </div>
            </div>
        </div>
    );
}

function FacilityCell({ facility }) {
    return (
        <div
            data-testid={`facility-${facility.name}`}
            className="bg-[#0a0a0b] hover:bg-[#141416] transition-colors p-6 group min-h-[140px] flex flex-col justify-between"
        >
            <facility.icon
                className="w-5 h-5 text-[#a1a1aa] group-hover:text-[#E53935] transition-colors mb-4"
                strokeWidth={1.75}
            />
            <div>
                <div className="font-display font-bold text-base text-white mb-1">
                    {facility.name}
                </div>
                <div className="font-mono-tech text-[11px] uppercase tracking-wide text-[#71717a]">
                    {facility.note}
                </div>
            </div>
        </div>
    );
}

function FacilityGrid() {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            <FieldPhotoCell />
            {FACILITIES.map((f) => (
                <FacilityCell key={f.name} facility={f} />
            ))}
        </div>
    );
}

function ArchitectCallout() {
    return (
        <div className="mt-12 grid lg:grid-cols-12 gap-px bg-white/5 border border-white/10">
            <div className="lg:col-span-7 bg-[#141416] p-8 md:p-12 flex flex-col justify-center">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935] mb-4">
                    For Architects & Mechanical Engineers
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight mb-4">
                    Spec it once. Pass inspection every time.
                </h3>
                <p className="text-sm md:text-base text-[#a1a1aa] leading-relaxed mb-6">
                    Single SKU. Self-contained terminals. No add-on modules. Drops into any plan
                    with a domestic hood and range up to 48". Documented to UL300a — printable
                    cut-sheets and PowerPoint install plans on request.
                </p>
                <a
                    href="#contact"
                    data-testid="architect-cta"
                    className="self-start inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 hover:border-[#E53935] hover:text-[#E53935] text-white text-sm font-medium rounded-sm transition-all"
                >
                    Request spec sheet
                </a>
            </div>
            <div className="lg:col-span-5 relative bg-[#0f0f12] overflow-hidden min-h-[300px]">
                <img
                    src={ARCH_IMG}
                    alt="Architects reviewing plans"
                    className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#141416] via-transparent to-transparent" />
            </div>
        </div>
    );
}

export default function Industries() {
    return (
        <section
            id="industries"
            data-testid="industries-section"
            className="relative py-20 md:py-24 bg-[#0a0a0b] border-t border-white/10"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <SectionHeader />
                <PartnersRow />
                <FacilityGrid />
                <ArchitectCallout />
            </div>
        </section>
    );
}
